// DistrictPicker — the one-time, skippable "Where do you play from?" sheet.
//
// Phase B (contract B6; plan §4.4): the weekly district service reads
// users.district_id, and a player whose district is NULL is silently rolled up
// to agent → state → nation. Live coverage was 88% on 2026-09-05, so this is
// NOT a gate — it is a nudge that ships anyway. Rules it must keep:
//
//   • NEVER blocks play. The game keeps running underneath; "Skip" and a
//     backdrop tap are always one thumb away; a failed request just closes it.
//   • Asked ONCE per account per device — localStorage 'bg.district.asked'
//     holds the ids of accounts already asked (comma-separated), so two
//     players sharing a phone each get exactly one ask.
//   • Shown only on entry to one of App.tsx DISTRICT_ASK_GAMES (the casino
//     games the district service serves, keyed by game id — NOT by the
//     lobby's display tag) and only when the server says district is NULL
//     (GET /api/me/board-visibility echoes state_id / district_id).
//   • Stays out of the way of the two global nudges: GeoForcedModal (a
//     verified player with NO location at all has the non-dismissible modal
//     up) and CompleteProfileModal (a legacy player with no name / birthdate
//     has that sheet up, z-80, mounted app-wide). Our sheet portals at z-9000
//     and would paint over either, so in both cases we simply don't show —
//     and don't mark asked, so the ask comes on a later casino entry once
//     those are done and the district is still NULL.
//   • Plain language, both themes, every tap target ≥ 44px, 16px inputs
//     (the shared `.input` / `.btn-*` utilities already guarantee this).
//
// Saving = PATCH /api/me/district { state_id, district_id } (the player api
// client has no `put`; the server binds PUT and PATCH to the same handler).
// Reads use the PUBLIC geo endpoints the signup form already uses.
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '@baaligames/shared-ui/lib/api';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import { isGeoLocated } from '@baaligames/shared-ui/components/LocationCapture';
import Icon from '@baaligames/shared-ui/components/Icon';

const ASKED_KEY = 'bg.district.asked';

type GeoRow = { id: number; name: string };
type BoardPrefs = { state_id: number | null; district_id: number | null };

function askedFor(uid: number | string): boolean {
  try {
    const raw = localStorage.getItem(ASKED_KEY);
    if (!raw) return false;
    return raw.split(',').includes(String(uid));
  } catch { return false; }
}

function markAsked(uid: number | string) {
  try {
    const raw = localStorage.getItem(ASKED_KEY);
    const ids = raw ? raw.split(',').filter(Boolean) : [];
    if (!ids.includes(String(uid))) ids.push(String(uid));
    localStorage.setItem(ASKED_KEY, ids.join(','));
  } catch { /* private mode / storage disabled — worst case we ask again */ }
}

// Title-case the UPPERCASE names the geo tables store ("MAHARASHTRA" →
// "Maharashtra") so the sheet reads like a sentence, not a form dump.
function pretty(name: string): string {
  return name.toLowerCase().replace(/(^|[\s(-])\S/g, (m) => m.toUpperCase());
}

export default function DistrictPicker() {
  const { user, refreshMe } = useAuth();
  const t = useT();
  // i18n keys resolve to themselves when a translation is missing; fall back
  // to plain English so nothing ever renders as a raw key.
  const tt = (key: string, fallback: string) => { const s = t(key); return s === key ? fallback : s; };

  const [open, setOpen] = useState(false);
  const [states, setStates] = useState<GeoRow[]>([]);
  const [districts, setDistricts] = useState<GeoRow[]>([]);
  const [stateId, setStateId] = useState<number | null>(null);
  const [districtId, setDistrictId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const uid = user?.id;

  // Decide ONCE per mount whether to ask. Cheap path first (local flags,
  // then the already-loaded /me user), and only then one GET to confirm
  // with the server that the district really is NULL.
  useEffect(() => {
    if (uid == null) return;
    if (askedFor(uid)) return;
    if (user?.district_id != null) { markAsked(uid); return; }
    // GeoForcedModal territory — see header. Not marked as asked: once they
    // finish that modal the district may still be NULL and we ask next time.
    const verified = !!(user?.email_verified_at || user?.telegram_verified_at);
    if (verified && !isGeoLocated(user)) return;
    // CompleteProfileModal territory — same predicate that modal uses
    // (name pair + birthdate, trimmed), same "not marked as asked" rule.
    const profileComplete = !!(
      (user?.first_name || '').trim() && (user?.last_name || '').trim() && (user?.birthdate || '').trim()
    );
    if (!profileComplete) return;
    let cancelled = false;
    (async () => {
      try {
        const prefs = await api.get<BoardPrefs>('/me/board-visibility');
        if (cancelled) return;
        if (prefs.district_id != null) { markAsked(uid); return; }
        const rows = await api.get<GeoRow[]>('/geo/states');
        if (cancelled) return;
        setStates(rows);
        if (prefs.state_id != null && rows.some((s) => s.id === prefs.state_id)) {
          setStateId(prefs.state_id);
        }
        setOpen(true);
      } catch { /* never nag on failure — try again on the next casino entry */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // Cascade: state → districts (public endpoint, browser-cached 1h).
  useEffect(() => {
    if (!open || stateId == null) { setDistricts([]); return; }
    let cancelled = false;
    api.get<GeoRow[]>(`/geo/districts?state_id=${stateId}`)
      .then((rows) => { if (!cancelled) setDistricts(rows); })
      .catch(() => { if (!cancelled) setDistricts([]); });
    return () => { cancelled = true; };
  }, [open, stateId]);

  const dismiss = useCallback(() => {
    if (uid != null) markAsked(uid);
    setOpen(false);
  }, [uid]);

  // Escape = skip (it is a nudge, never a wall).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dismiss]);

  async function save() {
    if (stateId == null || districtId == null || busy) return;
    setBusy(true); setErr(null);
    try {
      await api.patch('/me/district', { state_id: stateId, district_id: districtId });
      if (uid != null) markAsked(uid);
      setOpen(false);
      // Re-hydrate /me so user.district_id is current app-wide (fire-and-forget:
      // the sheet is already gone and the game never waited on us).
      refreshMe().catch(() => { /* non-fatal */ });
    } catch (e) {
      setErr((e as Error).message || tt('district.error', 'Could not save. You can skip for now.'));
    } finally {
      setBusy(false);
    }
  }

  if (!open || uid == null) return null;

  const canSave = stateId != null && districtId != null && !busy;

  // Portaled to <body>: game pages use transformed / fixed roots (the
  // .force-portrait wrapper is a containing block), which would clip or
  // rotate an in-tree overlay. Same reason LockedGameModal portals.
  return createPortal(
    <div
      className="fixed inset-0 z-[9000] bg-black/60 backdrop-blur-[2px]
                 flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="district-picker-title"
      onClick={dismiss}
    >
      <div
        className="w-full sm:max-w-md bg-white dark:bg-arena-900
                   rounded-t-3xl sm:rounded-2xl shadow-2xl
                   border border-arena-200/60 dark:border-arena-600/50
                   px-5 pt-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* grab handle — signals "sheet, swipe-away-able" on phones */}
        <div className="mx-auto w-10 h-1.5 rounded-full bg-arena-200 dark:bg-arena-600/70 sm:hidden" />

        <div className="flex items-start gap-3">
          <span className="grid place-items-center w-11 h-11 rounded-xl shrink-0
                           bg-neon-100 text-neon-700 dark:bg-neon-400/15 dark:text-neon-300">
            <Icon name="location_on" size={22} filled />
          </span>
          <div className="min-w-0">
            <h2 id="district-picker-title" className="text-lg font-extrabold text-arena-900 dark:text-white leading-tight">
              {tt('district.title', 'Where do you play from?')}
            </h2>
            <p className="text-[13px] text-arena-700 dark:text-neon-200/80 mt-0.5 leading-relaxed">
              {tt('district.subtitle', 'Pick your state and district so we can show you winners near you. Takes 5 seconds — or skip.')}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="label">{tt('district.state', 'State')}</span>
            <select
              className="input min-h-[48px]"
              value={stateId ?? ''}
              onChange={(e) => { setStateId(e.target.value ? Number(e.target.value) : null); setDistrictId(null); }}
              disabled={busy}
            >
              <option value="">{tt('district.pickState', 'Choose your state')}</option>
              {states.map((s) => <option key={s.id} value={s.id}>{pretty(s.name)}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="label">{tt('district.district', 'District')}</span>
            <select
              className="input min-h-[48px]"
              value={districtId ?? ''}
              onChange={(e) => setDistrictId(e.target.value ? Number(e.target.value) : null)}
              disabled={busy || stateId == null || districts.length === 0}
            >
              <option value="">
                {stateId == null
                  ? tt('district.pickStateFirst', 'Choose a state first')
                  : tt('district.pickDistrict', 'Choose your district')}
              </option>
              {districts.map((d) => <option key={d.id} value={d.id}>{pretty(d.name)}</option>)}
            </select>
          </label>
          {err && <p className="err">{err}</p>}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button type="button" className="btn-ghost" onClick={dismiss} disabled={busy}>
            {tt('district.skip', 'Skip for now')}
          </button>
          <button type="button" className="btn-primary" onClick={save} disabled={!canSave}>
            {busy ? tt('common.saving', 'Saving…') : tt('common.save', 'Save')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
